import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Loader2, Upload, FileText, Download, Trash2, Eye, Star, Plus, ArrowLeft, FileType, Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDER_CHEATSHEET = [
  { key: "{{ clinic.name }}", desc: "Clinic name" },
  { key: "{{ clinic.address }}", desc: "Clinic address" },
  { key: "{{ clinic.phone }}", desc: "Clinic phone" },
  { key: "{{ clinic.gstin }}", desc: "GSTIN" },
  { key: "{{ patient.name }}", desc: "Patient name" },
  { key: "{{ patient.age }}", desc: "Patient age" },
  { key: "{{ patient.gender }}", desc: "Patient gender" },
  { key: "{{ doctor.name }}", desc: "Doctor name" },
  { key: "{{ today }}", desc: "Today's date (YYYY-MM-DD)" },
  // Prescription
  { key: "{{ diagnosis }}", desc: "Prescription diagnosis", scope: "prescription" },
  { key: "{{ prescription.medicines_text }}", desc: "All medicines as plain text", scope: "prescription" },
  { key: "{% for m in prescription.medicines %}{{ m.name }} · {{ m.dosage }} · {{ m.frequency }} · {{ m.duration }}{% endfor %}", desc: "Loop over medicines (advanced)", scope: "prescription", advanced: true },
  { key: "{{ follow_up_date }}", desc: "Follow-up date", scope: "prescription" },
  // Invoice
  { key: "{{ invoice.number }}", desc: "Invoice number", scope: "invoice" },
  { key: "{{ invoice.amount_inr }}", desc: "Amount (₹600)", scope: "invoice" },
  { key: "{{ invoice.status }}", desc: "Paid / Unpaid", scope: "invoice" },
  { key: "{{ invoice.payment_method }}", desc: "Payment method", scope: "invoice" },
  { key: "{% for i in invoice.items %}{{ i.description }} - {{ i.amount }}{% endfor %}", desc: "Loop over line items", scope: "invoice", advanced: true },
];

export default function DocumentTemplates() {
  const [types, setTypes] = useState([]);
  const [overridable, setOverridable] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, l] = await Promise.all([
        api.get("/templates/types"),
        api.get("/templates"),
      ]);
      setTypes(t.data.types || []);
      setOverridable(t.data.overridable || []);
      setItems(l.data.items || []);
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const filtered = filterType === "all" ? items : items.filter((x) => x.doc_type === filterType);
    const m = {};
    filtered.forEach((it) => {
      const k = it.doc_type;
      if (!m[k]) m[k] = [];
      m[k].push(it);
    });
    return m;
  }, [items, filterType]);

  const labelFor = (key) => types.find((t) => t.key === key)?.label || key;

  const handleSetDefault = async (tpl, makeDefault) => {
    setBusyId(tpl.template_id);
    try {
      await api.put(`/templates/${tpl.template_id}`, { set_default: makeDefault });
      toast.success(makeDefault ? `Set as default ${labelFor(tpl.doc_type)} template` : "Removed default");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) return;
    setBusyId(tpl.template_id);
    try {
      await api.delete(`/templates/${tpl.template_id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const handlePreview = async (tpl, fmt) => {
    setBusyId(tpl.template_id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/templates/${tpl.template_id}/preview?format=${fmt}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (fmt === "pdf") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${tpl.name || "preview"}-sample.${fmt}`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Preview failed — check that placeholders are correct");
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (tpl) => {
    setBusyId(tpl.template_id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/templates/${tpl.template_id}/download`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tpl.name || "template"}.docx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto fade-in" data-testid="templates-page">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/settings" className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5 mb-3" data-testid="back-to-settings">
            <ArrowLeft className="w-3 h-3" /> Back to Settings
          </Link>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2">
            Branding
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Document templates
          </h1>
          <p className="text-slate-600 mt-1 max-w-2xl">
            Upload your own Word documents with placeholders like <span className="font-mono text-xs px-1 py-0.5 rounded bg-slate-100">{"{{ patient.name }}"}</span>. We fill in real data and convert to PDF when you print or share.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setHelpOpen(true)} data-testid="open-help-btn">
            <Sparkles className="w-4 h-4 mr-2" /> Placeholder guide
          </Button>
          <Button onClick={() => setUploadOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="open-upload-btn">
            <Plus className="w-4 h-4 mr-2" /> Upload template
          </Button>
        </div>
      </div>

      {/* Type filter */}
      <Card className="border-slate-200 mb-6">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-500">Filter by type:</span>
          <button
            onClick={() => setFilterType("all")}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border ${filterType === "all" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
            data-testid="filter-all"
          >
            All ({items.length})
          </button>
          {types.map((t) => {
            const c = items.filter((x) => x.doc_type === t.key).length;
            if (c === 0 && filterType !== t.key) return null;
            return (
              <button
                key={t.key}
                onClick={() => setFilterType(t.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border ${filterType === t.key ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                data-testid={`filter-${t.key}`}
              >
                {t.label} ({c})
              </button>
            );
          })}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="p-16 text-center" data-testid="templates-empty">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <FileType className="w-7 h-7 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "Manrope" }}>
              No templates yet
            </h3>
            <p className="text-sm text-slate-500 mb-5 max-w-sm mx-auto">
              Upload a .docx file with placeholders to brand your prescriptions, invoices and receipts. Set a default per type — we&apos;ll use it whenever you print or share.
            </p>
            <Button onClick={() => setUploadOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="empty-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload your first template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.keys(grouped).map((typeKey) => (
            <Card key={typeKey} className="border-slate-200" data-testid={`group-${typeKey}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  <FileText className="w-4 h-4 text-slate-500" /> {labelFor(typeKey)}
                  {overridable.includes(typeKey) && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] font-medium uppercase tracking-wider">
                      Auto-used on Print / Share PDF
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {grouped[typeKey].map((tpl) => (
                  <TemplateRow
                    key={tpl.template_id}
                    tpl={tpl}
                    busy={busyId === tpl.template_id}
                    onSetDefault={(v) => handleSetDefault(tpl, v)}
                    onDelete={() => handleDelete(tpl)}
                    onPreview={(fmt) => handlePreview(tpl, fmt)}
                    onDownload={() => handleDownload(tpl)}
                  />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} types={types} onUploaded={load} />
      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
}

function TemplateRow({ tpl, busy, onSetDefault, onDelete, onPreview, onDownload }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3 flex-wrap" data-testid={`tpl-row-${tpl.template_id}`}>
      <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
        <FileText className="w-5 h-5 text-slate-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-slate-900 truncate" data-testid={`tpl-name-${tpl.template_id}`}>{tpl.name}</div>
          {tpl.is_default && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`tpl-default-badge-${tpl.template_id}`}>
              <Star className="w-3 h-3 mr-1 fill-emerald-600 text-emerald-600" /> Default
            </Badge>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">
          {tpl.original_filename} · {(tpl.size / 1024).toFixed(1)} KB · {tpl.placeholders?.length || 0} placeholders
          {tpl.blocks?.length ? ` · ${tpl.blocks.length} blocks` : ""}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <Switch
            checked={!!tpl.is_default}
            onCheckedChange={onSetDefault}
            disabled={busy}
            data-testid={`tpl-default-switch-${tpl.template_id}`}
          />
          Default
        </label>
        <Button size="sm" variant="outline" onClick={() => onPreview("pdf")} disabled={busy} data-testid={`tpl-preview-pdf-${tpl.template_id}`} title="Preview PDF with sample data">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          <span className="hidden sm:inline ml-1">Preview</span>
        </Button>
        <Button size="sm" variant="ghost" onClick={onDownload} disabled={busy} className="h-8 w-8 p-0" data-testid={`tpl-download-${tpl.template_id}`} title="Download original .docx">
          <Download className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} disabled={busy} className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`tpl-delete-${tpl.template_id}`} title="Delete">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function UploadDialog({ open, onOpenChange, types, onUploaded }) {
  const [docType, setDocType] = useState("prescription");
  const [customType, setCustomType] = useState("");
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [setDefault, setSetDefault] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setDocType("prescription");
      setCustomType("");
      setFile(null);
      setName("");
      setSetDefault(true);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pick a .docx file"); return; }
    const finalType = docType === "__custom__" ? customType.trim() : docType;
    if (!finalType) { toast.error("Choose a document type"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", finalType);
      if (name.trim()) fd.append("name", name.trim());
      fd.append("set_default", setDefault ? "true" : "false");
      const { data } = await api.post("/templates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded "${data.name}" · ${data.placeholders?.length || 0} placeholders detected`);
      onOpenChange(false);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>Upload document template</DialogTitle>
          <DialogDescription>
            .docx files with Jinja-style placeholders like <span className="font-mono">{"{{ patient.name }}"}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs">Document type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="upload-doc-type-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.key} value={t.key} data-testid={`upload-doc-type-${t.key}`}>{t.label}</SelectItem>
                ))}
                <SelectItem value="__custom__" data-testid="upload-doc-type-custom">+ Custom type…</SelectItem>
              </SelectContent>
            </Select>
            {docType === "__custom__" && (
              <Input
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                placeholder="e.g. discharge-summary"
                className="mt-2 h-11"
                data-testid="upload-custom-type-input"
              />
            )}
          </div>
          <div>
            <Label className="text-xs">Template name (optional)</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Branded prescription v2"
              className="mt-1.5 h-11"
              maxLength={80}
              data-testid="upload-name-input"
            />
          </div>
          <div>
            <Label className="text-xs">.docx file</Label>
            <div className="mt-1.5">
              <input
                ref={inputRef}
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="upload-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                className="w-full justify-start"
                data-testid="upload-file-pick-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {file ? `${file.name} · ${(file.size / 1024).toFixed(1)} KB` : "Choose .docx file…"}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Max 5MB. Use Microsoft Word or LibreOffice. Keep each placeholder inside a single styled run.</p>
          </div>
          <label className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200 cursor-pointer">
            <Switch checked={setDefault} onCheckedChange={setSetDefault} data-testid="upload-set-default-switch" />
            <div className="text-sm">
              <div className="font-medium text-slate-900">Set as default</div>
              <div className="text-xs text-slate-500">Use this template automatically for Print / Share PDF.</div>
            </div>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={submitting || !file} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HelpDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="help-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>Placeholder guide</DialogTitle>
          <DialogDescription>
            Copy these tags into your .docx in Word or LibreOffice. Keep each tag inside one styled run (don&apos;t bold/italic half of it).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 pt-2">
          {PLACEHOLDER_CHEATSHEET.map((p) => (
            <div key={p.key} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-b-0">
              <code
                className="text-[11px] bg-slate-50 px-2 py-1 rounded font-mono text-slate-800 cursor-pointer hover:bg-slate-100 break-all max-w-[55%]"
                onClick={() => { navigator.clipboard?.writeText(p.key); toast.success("Copied"); }}
                title="Click to copy"
              >
                {p.key}
              </code>
              <div className="flex-1 text-xs text-slate-600 pt-1">
                {p.desc}
                {p.scope && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">[{p.scope}]</span>}
                {p.advanced && <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-600">[advanced]</span>}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
